import { useState } from 'react'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import PersonIcon from '@mui/icons-material/Person'
import { useNavigate } from 'react-router'
import { useSessionStore } from '../../state'
import routes from '../../pages/routes'
import AccreditationStatusBadge from '../AccreditationStatusBadge'
import ProfileRatingDisplay from '../ProfileRatingDisplay'
import { getInitials } from './getInitials'
import { truncateDisplayName } from './truncateDisplayName'

const ProfileCard = () => {
  const navigate = useNavigate()
  const user = useSessionStore(state => state.user)
  const profile = useSessionStore(state => state.profile)
  const status = profile?.accreditation?.status ?? null
  const ratingAggregates = profile?.ratingAggregates ?? {
    totalRatingsReceived: 0,
    averageTickScore: 0,
    perfectGuidedDiveRatings: 0
  }

  const [photoError, setPhotoError] = useState(false)

  const displayName = user?.displayName || null
  const email = user?.email || null
  const photoURL = user?.photoURL || null

  // Determine avatar content
  const renderAvatar = () => {
    const hasPhoto = photoURL && !photoError

    if (hasPhoto) {
      return (
        <Avatar
          src={photoURL}
          onError={() => setPhotoError(true)}
          sx={{ width: 48, height: 48 }}
        />
      )
    }

    if (displayName) {
      return (
        <Avatar sx={{ width: 48, height: 48 }}>
          {getInitials(displayName)}
        </Avatar>
      )
    }

    return (
      <Avatar sx={{ width: 48, height: 48 }}>
        <PersonIcon />
      </Avatar>
    )
  }

  // Determine identity text
  const renderIdentity = () => {
    if (!displayName && !email) {
      return (
        <Typography variant='subtitle1' fontWeight='bold'>
          No identity available
        </Typography>
      )
    }

    if (!displayName) {
      return (
        <>
          <Typography variant='subtitle1' fontWeight='bold'>
            Hello, diver
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {email}
          </Typography>
        </>
      )
    }

    return (
      <>
        <Typography variant='subtitle1' fontWeight='bold'>
          {truncateDisplayName(displayName, 50)}
        </Typography>
        {email && (
          <Typography variant='caption' color='text.secondary'>
            {email}
          </Typography>
        )}
      </>
    )
  }

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      {/* Identity header: avatar + name/email */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {renderAvatar()}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {renderIdentity()}
        </Box>
      </Box>

      {/* Accreditation section */}
      <Box sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AccreditationStatusBadge status={status} />
        </Box>

        {profile?.accreditation && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {profile.accreditation.type.toUpperCase()}
            {profile.accreditation.level &&
              ` \u2014 ${profile.accreditation.level}`}
          </Typography>
        )}

        {status === 'pending' && (
          <Typography variant='body2' color='text.secondary'>
            Your accreditation is being reviewed. We&apos;ll update you once
            it&apos;s been verified.
          </Typography>
        )}

        {status === 'verified' && (
          <Typography variant='body2' color='text.secondary'>
            You&apos;re a verified diver. You have full access to the platform.
          </Typography>
        )}

        {status === 'rejected' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              Your accreditation could not be verified. Please resubmit with
              correct details.
            </Typography>
            <Button
              variant='contained'
              size='small'
              onClick={() => navigate(routes.accreditation)}
            >
              Resubmit
            </Button>
          </Box>
        )}

        {status === null && (
          <Typography variant='body2' color='text.secondary'>
            You haven&apos;t submitted your accreditation yet. Head to the home
            screen to get started.
          </Typography>
        )}
      </Box>

      {/* Rating display section */}
      {profile && (
        <Box sx={{ mt: 2 }}>
          <ProfileRatingDisplay ratingAggregates={ratingAggregates} />
        </Box>
      )}
    </Paper>
  )
}

export default ProfileCard
